// ============================================================
// Shared user address form — wilayah + peta (sama dengan dashboard)
// ============================================================

import axios from '../config/axios.js';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export const DEFAULT_MAP_CENTER = [-0.462002, 100.401102];
export const DEFAULT_MAP_ZOOM = 15;

export function emptyAddress() {
  return {
    label: 'Rumah',
    recipient_name: '',
    phone: '',
    address_line: '',
    province_id: '',
    regency_id: '',
    district_id: '',
    village_id: '',
    postal_code: '',
    lat: null,
    lng: null,
    is_default: true,
  };
}

/**
 * @param {{ onSaved?: (address: object) => void|Promise<void> }} hooks
 */
export function createUserAddressForm(hooks = {}) {
  return {
    addressFormSaving: false,
    addressFormLoadingRegions: false,
    addressFormLocating: false,
    showAddressForm: false,
    addressForm: emptyAddress(),
    profilePhone: '',
    profileName: '',
    provinces: [],
    regencies: [],
    districts: [],
    villages: [],
    addressMap: null,
    addressMarker: null,

    async initAddressForm() {
      await this.loadProvinces();
      try {
        const { data } = await axios.get('/api/v1/profile');
        this.profilePhone = data.data?.phone || '';
        this.profileName = data.data?.name || '';
      } catch { /* silent */ }
      this.$watch?.('showAddressForm', (open) => {
        if (open) {
          this.$nextTick(() => this.initAddressMap());
        } else {
          this.destroyAddressMap();
        }
      });
    },

    openAddressForm() {
      this.addressForm = emptyAddress();
      if (this.profilePhone) {
        this.addressForm.phone = this.profilePhone;
      }
      if (this.profileName) {
        this.addressForm.recipient_name = this.profileName;
      }
      this.regencies = [];
      this.districts = [];
      this.villages = [];
      this.showAddressForm = true;
    },

    async loadProvinces() {
      try {
        const { data } = await axios.get('/api/v1/regions/provinces');
        this.provinces = data.data || [];
      } catch {
        this.provinces = [];
      }
    },

    async loadRegencies(provinceId) {
      this.regencies = [];
      this.districts = [];
      this.villages = [];
      if (!provinceId) return;
      this.addressFormLoadingRegions = true;
      try {
        const { data } = await axios.get('/api/v1/regions/regencies', { params: { province_id: provinceId } });
        this.regencies = data.data || [];
      } catch {
        this.regencies = [];
      } finally {
        this.addressFormLoadingRegions = false;
      }
    },

    async loadDistricts(regencyId) {
      this.districts = [];
      this.villages = [];
      if (!regencyId) return;
      this.addressFormLoadingRegions = true;
      try {
        const { data } = await axios.get('/api/v1/regions/districts', { params: { regency_id: regencyId } });
        this.districts = data.data || [];
      } catch {
        this.districts = [];
      } finally {
        this.addressFormLoadingRegions = false;
      }
    },

    async loadVillages(districtId) {
      this.villages = [];
      if (!districtId) return;
      this.addressFormLoadingRegions = true;
      try {
        const { data } = await axios.get('/api/v1/regions/villages', { params: { district_id: districtId } });
        this.villages = data.data || [];
      } catch {
        this.villages = [];
      } finally {
        this.addressFormLoadingRegions = false;
      }
    },

    async onAddressProvinceChange() {
      this.addressForm.regency_id = '';
      this.addressForm.district_id = '';
      this.addressForm.village_id = '';
      await this.loadRegencies(this.addressForm.province_id);
    },

    async onAddressRegencyChange() {
      this.addressForm.district_id = '';
      this.addressForm.village_id = '';
      await this.loadDistricts(this.addressForm.regency_id);
    },

    async onAddressDistrictChange() {
      this.addressForm.village_id = '';
      await this.loadVillages(this.addressForm.district_id);
    },

    initAddressMap() {
      const el = this.$refs?.addressMapEl;
      if (!el || this.addressMap) {
        if (this.addressMap) {
          setTimeout(() => this.addressMap.invalidateSize(), 80);
        }
        return;
      }

      const hasPoint = this.addressForm.lat != null && this.addressForm.lng != null
        && !Number.isNaN(Number(this.addressForm.lat)) && !Number.isNaN(Number(this.addressForm.lng));
      const center = hasPoint
        ? [Number(this.addressForm.lat), Number(this.addressForm.lng)]
        : DEFAULT_MAP_CENTER;

      this.addressMap = L.map(el, { scrollWheelZoom: true }).setView(center, DEFAULT_MAP_ZOOM);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
      }).addTo(this.addressMap);

      this.addressMarker = L.marker(center, { draggable: true }).addTo(this.addressMap);
      this.setAddressCoords(center[0], center[1], false);

      this.addressMarker.on('dragend', () => {
        const p = this.addressMarker.getLatLng();
        this.setAddressCoords(p.lat, p.lng);
      });

      this.addressMap.on('click', (e) => {
        this.addressMarker.setLatLng(e.latlng);
        this.setAddressCoords(e.latlng.lat, e.latlng.lng);
      });

      setTimeout(() => this.addressMap?.invalidateSize(), 120);
    },

    destroyAddressMap() {
      if (this.addressMap) {
        this.addressMap.remove();
        this.addressMap = null;
        this.addressMarker = null;
      }
    },

    setAddressCoords(lat, lng, notify = false) {
      this.addressForm.lat = Number(Number(lat).toFixed(6));
      this.addressForm.lng = Number(Number(lng).toFixed(6));
      if (notify) window.toast?.('Titik koordinat diperbarui.', 'success');
    },

    useMyLocationForAddress() {
      if (!navigator.geolocation) {
        window.toast?.('Browser tidak mendukung geolokasi.', 'error');
        return;
      }
      this.addressFormLocating = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          this.setAddressCoords(lat, lng, true);
          if (this.addressMap && this.addressMarker) {
            this.addressMarker.setLatLng([lat, lng]);
            this.addressMap.setView([lat, lng], 17);
          }
          this.addressFormLocating = false;
        },
        () => {
          window.toast?.('Gagal membaca lokasi. Izinkan akses lokasi di browser.', 'error');
          this.addressFormLocating = false;
        },
        { enableHighAccuracy: true, timeout: 15000 },
      );
    },

    async saveAddressForm() {
      const f = this.addressForm;
      if (!f.province_id || !f.regency_id || !f.district_id || !f.village_id) {
        window.toast?.('Lengkapi provinsi, kota/kabupaten, kecamatan, dan kelurahan.', 'error');
        return null;
      }
      if (f.lat == null || f.lng == null) {
        window.toast?.('Tentukan titik koordinat di peta.', 'error');
        return null;
      }

      this.addressFormSaving = true;
      try {
        const { data } = await axios.post('/api/v1/profile/addresses', f);
        window.toast?.('Alamat berhasil disimpan.', 'success');
        this.showAddressForm = false;
        if (hooks.onSaved) {
          await hooks.onSaved.call(this, data.data);
        }
        return data.data;
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan alamat.', 'error');
        return null;
      } finally {
        this.addressFormSaving = false;
      }
    },
  };
}
